import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import { Stat } from '../../api';
import { defaults, getDate } from './utils';

function processData(
  data: Stat,
  offset: number,
  daysInMonth: number,
  viewMode: string,
  day: number,
  getArrays: (data: Stat, viewMode: string) => Array<Array<number>>,
): Array<{ domain: number; value: number }> {
  let start: number, end: number, result: Array<number>;
  if (day) {
    start = 23 + offset + (day - 1) * 24;
    end = start + 24;
  } else {
    start = 23 + offset;
    end = 23 + offset + daysInMonth * 24;
  }
  const arrays: Array<Array<number>> = getArrays(data, viewMode); //TODO: add type
  if (arrays.length > 1) {
    if (arrays[0].length < end) {
      end = arrays[0].length;
    }
    if (day) {
      result = new Array<number>(end - start);
      for (let index = 0; index < result.length; index++) result[index] = 0;
      for (let index = start; index < end; index++) {
        const i = index - start;
        for (let index2 = 0; index2 < arrays.length; index2++) result[i] = arrays[index2][index] || 0;
        //for (const array in arrays) result[i] += array[index] || 0;
      }
    } else {
      result = new Array(Math.ceil((end - start) / 24));
      for (let index = 0; index < result.length; index++) result[index] = 0;
      for (let index = start; index < end; index++) {
        const i = Math.floor((index - start) / 24);
        for (const array in arrays) {
          result[i] += arrays[array][index] || 0;
        }
      }
    }
  } else {
    if (arrays[0].length < end) end = arrays[0].length;
    if (day) {
      result = arrays[0].slice(start, end);
    } else {
      result = new Array(Math.ceil((end - start) / 24));
      for (let index = 0; index < result.length; index++) result[index] = 0;

      for (let index = start; index < end; index++) {
        const i = Math.floor((index - start) / 24);
        result[i] += arrays[0][index] || 0;
      }
    }
  }
  return result.map(function (value, index) {
    const domain: number = day ? index : index + 1;
    return { domain: domain, value: value };
  });
}

export default function MonthBarChart({
  stat,
  offset,
  daysInMonth,
  viewMode,
  day,
  month,
  year,
  tz,
  options,
  onChangeDay,
  getUnit,
  getTitle,
  getArrays,
}: {
  stat: Stat;
  offset: number;
  daysInMonth: number;
  viewMode: string;
  day: number;
  month: number;
  year: number;
  tz: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any;
  onChangeDay: (d: number) => void;
  getUnit: (viewMode: string, day: number | undefined) => string;
  getTitle: (viewMode: string, day: number) => string;
  getArrays: (data: Stat, viewMode: string) => Array<Array<number>>;
}) {
  options = Object.assign(defaults, options);
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = d3.select(ref.current);
    const _data: Array<{ domain: number; value: number }> = processData(
      stat,
      offset,
      daysInMonth,
      viewMode,
      day,
      getArrays,
    );
    const total = _data.reduce(function (acc: number, d: { domain: number; value: number }) {
      return acc + d.value;
    }, 0);
    const margin = options.margin;
    const innerWidth = options.width - margin.left - margin.right;
    const innerHeight = options.width / options.aspectRatio - margin.top - margin.bottom;
    svg.selectAll('*').remove();
    svg
      .attr('width', options.width)
      .attr('height', options.width / options.aspectRatio)
      .style('display', 'block')
      .style('margin', 'auto');
    svg
      .append('g')
      .append('text')
      .text(getTitle(viewMode, day))
      .attr('transform', `translate(${options.width}, 20)`)
      .style('text-anchor', 'end')
      .style('font-weight', 'bold')
      .style('font-family', 'arial');
    svg
      .append('g')
      .append('text')
      .text(`${Math.floor(total)} ${getUnit(viewMode, day)}`)
      .attr('transform', `translate(${options.width}, 52)`)
      .style('text-anchor', 'end')
      .style('font-family', 'arial')
      .style('font-size', '12px')
      .style('font-weight', 'bold');
    svg
      .append('g')
      .append('text')
      .text(getDate(day, month, year, tz))
      .attr('transform', `translate(${options.width}, 35)`)
      .style('text-anchor', 'end')
      .style('font-family', 'arial')
      .style('font-size', '10px');
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    const xScale = d3
      .scaleBand()
      .range([0, innerWidth])
      .domain(
        day ? Array.from({ length: 24 }, (_, i) => `${i}`) : Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`),
      )
      .padding(0.2);
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('transform', 'translate(-10,0)rotate(-45)')
      .style('text-anchor', 'end');
    let max = d3.max(_data, (datum): number => Math.ceil((datum.value * 1.1) / 100) * 100);
    max = Math.max(Number(max), 100);
    const yScale = d3.scaleLinear().domain([0, max]).range([innerHeight, 0]);
    g.append('g').call(d3.axisLeft(yScale));
    g.selectAll('mybar')
      .data(_data)
      .enter()
      .append('rect')
      .attr('x', (d) => xScale(d.domain.toString()) || 0)
      .attr('y', (d) => yScale(d.value))
      .attr('width', xScale.bandwidth())
      .attr('height', (d) => innerHeight - yScale(d.value))
      .attr('fill', '#69b3a2')
      .on('mouseover', (e, d: { domain: number; value: number }) => {
        d3.select(e.target).attr('fill', 'orange');
        g.append('text')
          .text(`${d.domain}: ${Math.floor(d.value)}`)
          .attr('x', xScale(d.domain.toString()) || 0)
          .attr('y', yScale(d.value) - 15)
          .attr('id', `label-${d.domain}`);
      })
      .on('mouseout', function (e, d) {
        d3.select(this).attr('fill', '#69b3a2');
        d3.select(`#label-${d.domain}`).remove();
      })
      .on('click', (e, d) => {
        onChangeDay(day ? 0 : d.domain);
      });
  }, [
    stat,
    day,
    daysInMonth,
    offset,
    options.aspectRatio,
    options.margin,
    options.width,
    tz,
    viewMode,
    year,
    month,
    onChangeDay,
    getTitle,
    getUnit,
    getArrays,
  ]);
  return <svg ref={ref}></svg>;
}
